import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestYieldCallback, getViewParameterValue } from "@theme/utilities";

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {HTMLElement | null} */
  #overlay = null;

  /** @type {boolean} */
  isOverlayOpen = false;

  connectedCallback() {
    super.connectedCallback();

    // Event delegation for trigger button (remains inside component)
    this.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      if (
        event.target.closest(".variant-picker-summary__button") ||
        event.target.closest(".button-change-variant")
      ) {
        this.toggleOverlay(true);
      }
    });

    // Handle change events from size dropdown and other inputs outside overlay
    this.addEventListener("change", this.variantChanged.bind(this));

    this.moveOverlay();
  }

  disconnectedCallback() {
    // Clean up overlay when component is destroyed
    if (this.#overlay && document.body.contains(this.#overlay)) {
      document.body.removeChild(this.#overlay);
    }
  }

  moveOverlay() {
    const overlay = this.querySelector(".variant-picker-overlay");
    if (overlay) {
      // If we already have an overlay tracked and it's in body, remove it (cleanup)
      if (
        this.#overlay &&
        this.#overlay !== overlay &&
        document.body.contains(this.#overlay)
      ) {
        document.body.removeChild(this.#overlay);
      }

      this.#overlay = /** @type {HTMLElement} */ (overlay);
      document.body.appendChild(overlay);

      // Initialize internal state based on the new overlay DOM
      this.initFieldsets();

      // Add close listeners
      overlay.addEventListener("click", (event) => {
        if (!(event.target instanceof Element)) return;
        if (
          event.target.closest(".variant-picker-overlay__close") ||
          event.target.closest(".variant-picker-modal__close") ||
          event.target.closest(".variant-picker-overlay__backdrop") ||
          event.target.closest(".variant-picker-modal__backdrop")
        ) {
          this.toggleOverlay(false);
        } else if (
          event.target.closest(".variant-picker-overlay__confirm") ||
          event.target.closest(".variant-picker-modal__confirm")
        ) {
          this.toggleOverlay(false);
        }
      });

      // Forward change events since overlay is no longer a child
      overlay.addEventListener("change", this.variantChanged.bind(this));
    }
  }

  initFieldsets() {
    this.#radios = [];
    this.#checkedIndices = [];

    if (!this.#overlay) return;

    const fieldsets = Array.from(this.#overlay.querySelectorAll("fieldset"));

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.dataset.currentChecked === "true",
      );
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });
  }

  /**
   * Toggles the overlay visibility.
   * @param {boolean} show - Whether to show the overlay.
   */
  toggleOverlay(show) {
    const overlay = this.#overlay;
    if (overlay) {
      overlay.classList.toggle("is-visible", show);
      this.isOverlayOpen = show;

      // Prevent body scroll when overlay is open
      if (show) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    // Sync other inputs FIRST so that getAllInputs() returns consistent state for buildRequestUrl
    this.syncInputStates(selectedOption);

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const requestUrl = this.buildRequestUrl(selectedOption);

    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * Syncs the state of inputs that represent the same option across main view and overlay.
   * @param {HTMLElement} changedInput - The input that triggered the change.
   */
  syncInputStates(changedInput) {
    // We need the option ID (e.g. "Option 3" or position index) to identify siblings.
    // data-input-id format: "{position}-{loopIndex}" (e.g. "1-0").
    // The first part "1" uniquely identifies the Product Option (e.g. Size).
    const changedInputId = changedInput.dataset.inputId;
    const optionPosition = changedInputId ? changedInputId.split("-")[0] : null;

    if (!optionPosition) return;

    const allInputs = this.getAllInputs();
    const newValueId = changedInput.dataset.optionValueId;

    allInputs.forEach((input) => {
      // Skip the element that actually changed
      if (input === changedInput) return;

      const inputId = input.dataset.inputId;
      if (inputId && inputId.startsWith(`${optionPosition}-`)) {
        // This input belongs to the same Product Option (e.g. Size).
        // Check if it matches the new value.
        const isMatch = input.dataset.optionValueId === newValueId;

        if (input instanceof HTMLInputElement) {
          // Radio
          input.checked = isMatch;
          // Update helper attributes for styling if needed
          if (isMatch) input.dataset.currentChecked = "true";
          else input.dataset.currentChecked = "false";
        } else if (input instanceof HTMLOptionElement) {
          // Select Option
          if (isMatch) {
            const select = input.closest("select");
            if (select) select.value = input.value;
            input.selected = true;
          } else {
            input.selected = false;
          }
        }
      }
    });
  }

  /**
   * Helper to get all relevant inputs from both this component and the overlay.
   * @returns {HTMLElement[]} Array of inputs and options.
   */
  getAllInputs() {
    const roots = [this];
    if (this.#overlay) roots.push(this.#overlay);

    let all = [];
    roots.forEach((root) => {
      all = all.concat(
        Array.from(root.querySelectorAll("select option, fieldset input")),
      );
    });
    return all;
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      // Search in all inputs
      const targetElement = this.getAllInputs().find(
        (el) => el.dataset.optionValueId === target,
      );
      if (!targetElement) throw new Error("Target element not found");
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        // Use locally queried fieldsets from overlay OR main depending on where target is
        // Actually, internal styling logic relies on fieldset structure.
        // We find the parent fieldset of the target.
        const fieldset = target.closest("fieldset");
        if (fieldset) {
          // We might need to handle the pill logic locally for this fieldset
          // Re-implementing pill logic strictly for the target's container
          const radios = Array.from(fieldset.querySelectorAll("input"));
          // ... (Existing pill logic is complex and relies on #checkedIndices state which is indexed by fieldsetIndex)
          // If we have duplicate fieldsets (one in main, one in overlay), the index might conflict or be same.
          // For now, let's trust standard behavior if it's in overlay.
          // If it's a standalone dropdown, it doesn't use pill logic.
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      // Update selected attribute
      Array.from(target.options).forEach((opt) =>
        opt.removeAttribute("selected"),
      );
      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }

    // Default: also ensure we fetch the current section (usually product-information) so the media gallery is rendered with the new variant in the HTML response
    let sectionId = this.closest(".shopify-section")?.id?.replace(
      "shopify-section-",
      "",
    );
    if (!sectionId) {
      const mainSection = document.querySelector("main .shopify-section");
      if (mainSection && mainSection.id)
        sectionId = mainSection.id.replace("shopify-section-", "");
      else sectionId = "product-information";
    }
    params.push(`section_id=${sectionId}`);

    console.log("Variant buildRequestUrl fetched for section: ", sectionId);

    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          const variantData = JSON.parse(textContent);

          // EXTRACTION: Update the overlay image manually using the JSON data!
          if (variantData && variantData.featured_image && this.#overlay) {
            const currentImg = this.#overlay.querySelector(
              ".variant-picker-modal__media img",
            );
            if (currentImg) {
              // Shopify's variant featured_image doesn't have an exact sized URL sometimes, but it gives the raw URL
              // We'll append a width parameter if needed or just use the raw one
              let newSrc = variantData.featured_image.src;
              currentImg.src = newSrc;
              currentImg.srcset = newSrc; // Invalidate any srcset that was locking it
              if (variantData.featured_image.alt)
                currentImg.alt = variantData.featured_image.alt;
            }
          }

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              }),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Save the new grid values BEFORE morph (from the server response)
    const newGridValues = this.extractGridValues(newVariantPickerSource);

    // EXTRACTION: Update the overlay image manually since the overlay was moved to body and isn't morphed by morph(this)
    const newOverlay = newVariantPickerSource.querySelector(
      ".variant-picker-overlay",
    );
    if (newOverlay && this.#overlay) {
      const newImg = newOverlay.querySelector(
        ".variant-picker-modal__media img",
      );
      const currentImg = this.#overlay.querySelector(
        ".variant-picker-modal__media img",
      );
      if (newImg && currentImg && currentImg.src !== newImg.src) {
        currentImg.src = newImg.src;
        if (newImg.srcset) currentImg.srcset = newImg.srcset;
        if (newImg.alt) currentImg.alt = newImg.alt;
      }
    }

    morph(this, newVariantPickerSource);

    // Re-initialize overlay after morph (moves overlay to body)
    this.moveOverlay();

    // Apply the saved grid values AFTER morph and moveOverlay
    if (newGridValues) {
      this.applyGridValues(newGridValues);
    }

    // Restore overlay visibility if it was open
    if (this.isOverlayOpen) {
      this.toggleOverlay(true);
    }

    return newProduct;
  }

  /**
   * Extracts variant-info-grid values from a source element.
   * @param {Element} source - The source element containing the grid.
   * @returns {Map<string, string> | null} Map of option names to values, or null if no grid found.
   */
  extractGridValues(source) {
    const grid = source.querySelector(".variant-info-grid");
    if (!grid) return null;

    const values = new Map();
    const valueElements = grid.querySelectorAll(".variant-info-value");
    valueElements.forEach((el, index) => {
      const element = /** @type {HTMLElement} */ (el);
      const name = element.dataset.optionName || `index-${index}`;
      values.set(name, element.textContent || "");
    });
    return values;
  }

  /**
   * Applies saved grid values to the current component's variant-info-grid.
   * @param {Map<string, string>} values - Map of option names to values.
   */
  applyGridValues(values) {
    const grid = this.querySelector(".variant-info-grid");
    if (!grid) return;

    const valueElements = grid.querySelectorAll(".variant-info-value");
    valueElements.forEach((el, index) => {
      const element = /** @type {HTMLElement} */ (el);
      const name = element.dataset.optionName || `index-${index}`;
      if (values.has(name)) {
        element.textContent = values.get(name) || "";
      }
    });
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOptions = this.getAllInputs().filter(
      (el) =>
        (el instanceof HTMLInputElement && el.checked) ||
        (el instanceof HTMLOptionElement && el.selected),
    );
    return /** @type {HTMLInputElement | HTMLOptionElement} */ (
      selectedOptions[0]
    );
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const selectedOptions = this.getAllInputs().filter(
      (el) =>
        (el instanceof HTMLInputElement && el.checked) ||
        (el instanceof HTMLOptionElement && el.selected),
    );

    // Deduplicate by Option Position/Name to avoid sending multiple values for same option
    const distinctMap = new Map();

    selectedOptions.forEach((opt) => {
      const inputId = opt.dataset.inputId; // e.g. "1-0"
      if (inputId) {
        const position = inputId.split("-")[0];
        // If we have multiple selections for the same position, the LAST one in DOM order currently wins (or first? Map preserves insertion).
        // Sync logic should typically ensure they are same. If not, last change logic in syncInputStates should have fired.
        // But here, we just need unique IDs.
        distinctMap.set(position, opt.dataset.optionValueId);
      } else {
        // Fallback for elements without input-id?
        if (opt.dataset.optionValueId)
          distinctMap.set(opt.dataset.optionValueId, opt.dataset.optionValueId);
      }
    });

    return Array.from(distinctMap.values());
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
